import { zodResolver } from '@hookform/resolvers/zod';
import {
  customersControllerFindContacts,
  inspectionsControllerAssignToMe,
  inspectionsControllerRequestPledgeStudy,
  inspectionsControllerSubmitApproval,
  paymentsControllerSendPaymentLink,
  pledgeStudyControllerSubmitApproval,
  requestsControllerApprove,
  requestsControllerDownload,
  requestsControllerFindOne,
  requestsControllerIssuePet,
  requestsControllerReject,
  requestsControllerRemove,
  requestsControllerSendToSap,
  requestsControllerSubmitForApproval,
  requestsControllerSubmitForInspection,
} from '@kalba/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock,
  Download,
  ExternalLink,
  Home,
  Mail,
  Pencil,
  Send,
  Trash2,
  UserCircle,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AppLayout } from '../../../components/layout/app-layout';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { DirhamIcon, Price } from '../../../components/ui/price';
import { StatusChevron } from '../../../components/ui/status-chevron';
import { Tabs } from '../../../components/ui/tabs';
import { usePermissions } from '../../../hooks/use-permissions';
import { useAuthStore } from '../../../stores/auth.store';
import { usePermitDetailStore } from '../../../stores/permit-detail.store';
import { usePledgeStudyTabStore } from '../../../stores/pledge-study-tab.store';
import { ApprovalHistoryTab } from './components/ApprovalHistoryTab';
import { AuditLogTab } from './components/AuditLogTab';
import { DetailsTab } from './components/DetailsTab';
import { DocumentsTab } from './components/DocumentsTab';
import { FeesTab } from './components/FeesTab';
import { InspectionTab } from './components/InspectionTab';
import { PledgeStudyTab } from './components/PledgeStudyTab';
import type { PermitDetail } from './components/types';

const tabSearchSchema = z.object({
  tab: z
    .enum(['details', 'fees', 'documents', 'inspection', 'pledge', 'approval-history', 'audit'])
    .optional(),
});

export const Route = createFileRoute('/permits/$permitId/')({
  validateSearch: tabSearchSchema,
  beforeLoad: () => {
    const { user, permissions } = useAuthStore.getState();
    if (!user) throw redirect({ to: '/login' });
    if (!permissions.includes('permit:view')) throw redirect({ to: '/dashboard' });
  },
  component: PermitDetailPage,
});

// ── Status config ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; className: string; showClock?: boolean }> = {
  DRAFT: {
    label: 'Draft',
    className:
      'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30',
    showClock: true,
  },
  SUBMITTED_FOR_INSPECTION: {
    label: 'Inspection Requested',
    className:
      'bg-blue-500/10 text-blue-700 border-blue-500/20 dark:bg-blue-500/20 dark:text-blue-300 dark:border-blue-500/30',
  },
  INSPECTION_IN_PROGRESS: {
    label: 'Inspection In Progress',
    className:
      'bg-yellow-500/10 text-yellow-700 border-yellow-500/20 dark:bg-yellow-500/20 dark:text-yellow-300 dark:border-yellow-500/30',
  },
  PLEDGE_STUDY_REQUEST: {
    label: 'Pledge Study',
    className:
      'bg-orange-500/10 text-orange-700 border-orange-500/20 dark:bg-orange-500/20 dark:text-orange-300 dark:border-orange-500/30',
  },
  APPROVAL_REQUEST: {
    label: 'Approval',
    className:
      'bg-purple-500/10 text-purple-700 border-purple-500/20 dark:bg-purple-500/20 dark:text-purple-300 dark:border-purple-500/30',
  },
  APPROVED: {
    label: 'Approved',
    className:
      'bg-teal-500/10 text-teal-700 border-teal-500/20 dark:bg-teal-500/20 dark:text-teal-300 dark:border-teal-500/30',
  },
  REJECTED: {
    label: 'Rejected',
    className:
      'bg-red-500/10 text-red-700 border-red-500/20 dark:bg-red-500/20 dark:text-red-300 dark:border-red-500/30',
  },
  PAYMENT_PROCESS: {
    label: 'Payment',
    className:
      'bg-amber-500/10 text-amber-700 border-amber-500/20 dark:bg-amber-500/20 dark:text-amber-300 dark:border-amber-500/30',
  },
  PAID: {
    label: 'Paid',
    className:
      'bg-lime-500/10 text-lime-700 border-lime-500/20 dark:bg-lime-500/20 dark:text-lime-300 dark:border-lime-500/30',
  },
  PERMIT_ISSUED: {
    label: 'Issued',
    className:
      'bg-green-500/10 text-green-700 border-green-500/20 dark:bg-green-500/20 dark:text-green-300 dark:border-green-500/30',
  },
  SENT_TO_SAP: {
    label: 'SAP Synced',
    className:
      'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 dark:bg-emerald-500/20 dark:text-emerald-300 dark:border-emerald-500/30',
  },
};

const PERMIT_STATUS_CHAIN = [
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SUBMITTED_FOR_INSPECTION', label: 'Inspection Requested' },
  { key: 'INSPECTION_IN_PROGRESS', label: 'Inspection In Progress' },
  { key: 'PLEDGE_STUDY_REQUEST', label: 'Pledge Study' },
  { key: 'APPROVAL_REQUEST', label: 'Approval' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'PAYMENT_PROCESS', label: 'Payment' },
  { key: 'PAID', label: 'Paid' },
  { key: 'PERMIT_ISSUED', label: 'Issued' },
  { key: 'SENT_TO_SAP', label: 'SAP Synced' },
] as const;

const ISSUED_STATUSES = ['PERMIT_ISSUED', 'SENT_TO_SAP'];

const commentSchema = z.object({
  comments: z.string().min(1, 'Comment is required'),
});
type CommentValues = z.infer<typeof commentSchema>;

type Tab =
  | 'details'
  | 'fees'
  | 'documents'
  | 'inspection'
  | 'pledge'
  | 'approval-history'
  | 'audit';

// ── Helpers ────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? {
    label: status,
    className: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${cfg.className}`}
    >
      {cfg.showClock && <Clock className="w-3 h-3" />}
      {cfg.label}
    </span>
  );
}

function StatusProgress({ status }: { status: string }) {
  if (status === 'REJECTED') {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-error-50 border border-error-200 px-4 py-3">
        <span className="h-2 w-2 rounded-full bg-error-500" />
        <span className="text-sm font-medium text-error-600">Rejected</span>
      </div>
    );
  }

  return <StatusChevron steps={PERMIT_STATUS_CHAIN} currentKey={status} />;
}

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

// ── Page component ─────────────────────────────────────────────────────────

function PermitDetailPage() {
  const { permitId } = Route.useParams();
  const { tab: tabFromUrl } = Route.useSearch();
  const { user } = useAuthStore();
  const { can, canViewField } = usePermissions();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const {
    activeTab,
    approveModal,
    rejectModal,
    deleteModal,
    actionError,
    setActiveTab,
    setApproveModal,
    setRejectModal,
    setDeleteModal,
    setActionError,
    reset: resetStore,
  } = usePermitDetailStore();

  const { setCreateOpen: setPledgeCreateOpen } = usePledgeStudyTabStore();

  const [inspectionFormStarted, setInspectionFormStarted] = useState(false);
  const [sendLinkModal, setSendLinkModal] = useState(false);

  useEffect(() => {
    return () => resetStore();
  }, [permitId]);

  useEffect(() => {
    if (tabFromUrl) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  const approveForm = useForm<CommentValues>({
    resolver: zodResolver(commentSchema),
    defaultValues: { comments: '' },
  });

  const rejectForm = useForm<CommentValues>({
    resolver: zodResolver(commentSchema),
    defaultValues: { comments: '' },
  });

  const permitKey = ['permit', permitId];

  const {
    data: permit,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: permitKey,
    queryFn: () => requestsControllerFindOne({ id: permitId }) as unknown as Promise<PermitDetail>,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: permitKey });

  function extractApiError(err: unknown): string {
    if (err && typeof err === 'object' && 'body' in err) {
      const body = (err as { body: unknown }).body;
      if (body && typeof body === 'object' && 'message' in body) {
        const b = body as { message: string; missing?: string[] };
        return b.missing?.length ? `${b.message}: ${b.missing.join(', ')}` : b.message;
      }
    }
    return 'Something went wrong. Please try again.';
  }

  const submitInspection = useMutation({
    mutationFn: () => requestsControllerSubmitForInspection({ id: permitId }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(extractApiError(err)),
  });

  const submitApprovalMutation = useMutation({
    mutationFn: () => requestsControllerSubmitForApproval({ id: permitId }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(extractApiError(err)),
  });

  const submitPledgeApprovalMutation = useMutation({
    mutationFn: () => pledgeStudyControllerSubmitApproval({ permitId }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(extractApiError(err)),
  });

  const assignToMe = useMutation({
    mutationFn: () => inspectionsControllerAssignToMe({ permitId }),
    onSuccess: invalidate,
  });

  const submitInspectionApproval = useMutation({
    mutationFn: () => inspectionsControllerSubmitApproval({ permitId }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(extractApiError(err)),
  });

  const requestPledgeMutation = useMutation({
    mutationFn: () => inspectionsControllerRequestPledgeStudy({ permitId }),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(extractApiError(err)),
  });

  const approveMutation = useMutation({
    mutationFn: (data: CommentValues) =>
      requestsControllerApprove({ id: permitId, requestBody: { comments: data.comments } }),
    onSuccess: () => {
      invalidate();
      setApproveModal(false);
      approveForm.reset();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (data: CommentValues) =>
      requestsControllerReject({ id: permitId, requestBody: { comments: data.comments } }),
    onSuccess: () => {
      invalidate();
      setRejectModal(false);
      rejectForm.reset();
    },
  });

  const issueMutation = useMutation({
    mutationFn: () => requestsControllerIssuePet({ id: permitId }),
    onSuccess: invalidate,
  });

  const sapMutation = useMutation({
    mutationFn: () => requestsControllerSendToSap({ id: permitId }),
    onSuccess: invalidate,
  });

  const downloadMutation = useMutation({
    mutationFn: () =>
      requestsControllerDownload({ id: permitId }) as unknown as Promise<{
        downloadUrl: string;
        expiresAt: string;
      }>,
    onSuccess: (data) => window.open(data.downloadUrl, '_blank'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => requestsControllerRemove({ id: permitId }),
    onSuccess: () => {
      setDeleteModal(false);
      qc.invalidateQueries({ queryKey: ['permits'] });
      navigate({ to: '/permits', search: { page: 1, limit: 20 } });
    },
    onError: (err) => setActionError(extractApiError(err)),
  });

  // ── Loading / error ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-96 animate-pulse rounded bg-gray-100" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-100" />
        </div>
      </AppLayout>
    );
  }

  if (isError || !permit) {
    return (
      <AppLayout>
        <div className="py-20 text-center">
          <p className="mb-3 text-red-500">Failed to load permit</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="text-sm font-medium text-brand-600 underline hover:text-brand-700"
          >
            Retry
          </button>
        </div>
      </AppLayout>
    );
  }

  // ── Action visibility ──────────────────────────────────────────────────

  const customerOwnsPermit = permit.contact.userId === user?.id;
  const isIssued = ISSUED_STATUSES.includes(permit.status);
  const isEnvRenewal =
    permit.requestTypeConfig.code === 'ENV' && permit.requestAction === 'RENEWAL';

  const showEdit = can('permit:create') && permit.status === 'DRAFT';
  const showDelete = can('permit:delete') && permit.status === 'DRAFT';

  const showSubmitInspection =
    can('permit:submit-inspection') &&
    permit.status === 'DRAFT' &&
    permit.requestTypeConfig.hasInspection;

  const showSubmitApproval =
    can('permit:submit-inspection') &&
    permit.status === 'DRAFT' &&
    (!permit.requestTypeConfig.hasInspection ||
      permit.requestTypeConfig.code === 'VEH' ||
      isEnvRenewal);

  const showAssignToMe =
    can('permit:assign-inspection') && permit.status === 'SUBMITTED_FOR_INSPECTION';

  const hasInspectionContent = !!(
    permit.inspection?.findings || permit.inspection?.recommendations
  );

  const showCreateInspection =
    !!permit.inspection &&
    permit.inspection.assignedInspectorId === user?.id &&
    permit.inspection.status === 'IN_PROGRESS' &&
    !hasInspectionContent &&
    !inspectionFormStarted;

  const showSubmitInspectionApproval =
    !!permit.inspection &&
    permit.inspection.assignedInspectorId === user?.id &&
    permit.inspection.status === 'IN_PROGRESS' &&
    hasInspectionContent &&
    can('permit:submit-inspection');

  const showRequestPledge =
    !!permit.inspection &&
    permit.inspection.assignedInspectorId === user?.id &&
    permit.inspection.status === 'IN_PROGRESS' &&
    hasInspectionContent &&
    permit.requestTypeConfig.hasPledgeStudy &&
    can('permit:request-pledge');

  const showCreatePledge =
    can('pledge:create') &&
    permit.status === 'PLEDGE_STUDY_REQUEST' &&
    !permit.pledgeStudy &&
    permit.requestTypeConfig.hasPledgeStudy;

  const showSubmitPledgeApproval =
    can('pledge:submit') &&
    permit.status === 'PLEDGE_STUDY_REQUEST' &&
    !!permit.pledgeStudy &&
    permit.pledgeStudy.status === 'DRAFT';

  const showApprove = can('permit:approve') && permit.status === 'APPROVAL_REQUEST';
  const showReject = can('permit:reject') && permit.status === 'APPROVAL_REQUEST';
  const showPay = can('permit:pay') && permit.status === 'PAYMENT_PROCESS';
  const showSendPaymentLink =
    can('permit:send-payment-link') && permit.status === 'PAYMENT_PROCESS';
  const showIssue = can('permit:issue') && permit.status === 'PAID';
  const showSendToSap =
    can('permit:send-to-sap') &&
    permit.status === 'PERMIT_ISSUED' &&
    permit.sapSync.status !== 'SUCCESS';
  const showDownload = isIssued && (can('permit:issue') || customerOwnsPermit);

  // ── Tab visibility ─────────────────────────────────────────────────────

  const showInspection = permit.requestTypeConfig.hasInspection || !!permit.inspection;
  const showPledge = permit.requestTypeConfig.hasPledgeStudy;
  const showAudit = can('inspection:view');
  const showFees = canViewField('permit', 'feeBreakdown') && !!permit.feeBreakdown;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'details', label: 'Permit Information' },
    ...(showFees ? [{ id: 'fees' as Tab, label: 'Fee Breakdown' }] : []),
    { id: 'documents', label: 'Documents' },
    ...(showInspection ? [{ id: 'inspection' as Tab, label: 'Inspection' }] : []),
    ...(showPledge ? [{ id: 'pledge' as Tab, label: 'Pledge & Study' }] : []),
    { id: 'approval-history', label: 'Approval History' },
    ...(showAudit ? [{ id: 'audit' as Tab, label: 'Activity Log' }] : []),
  ];

  return (
    <AppLayout>
      {/* Breadcrumb */}
      <nav className="mb-5 flex items-center gap-1.5 text-xs text-gray-400">
        <button
          type="button"
          onClick={() => navigate({ to: '/dashboard' })}
          className="shrink-0 transition-colors hover:text-gray-600"
        >
          <Home size={12} />
        </button>
        <ChevronRight size={12} className="shrink-0" />
        <button
          type="button"
          onClick={() => navigate({ to: '/permits', search: { page: 1, limit: 20 } })}
          className="transition-colors hover:text-gray-600 font-medium"
        >
          Permits
        </button>
        <ChevronRight size={12} className="shrink-0" />
        <span className="font-medium text-gray-600">{permit.referenceNo}</span>
      </nav>

      {/* Header card */}
      <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-white to-gray-50/50 shadow-theme-sm">
        <div className="p-8">
          {/* Title row */}
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-6">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-semibold tracking-tight text-gray-900">
                  {permit.referenceNo}
                </h1>
                <StatusBadge status={permit.status} />
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-xl border border-brand-200 bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/20 dark:text-brand-300">
                  🏢 {permit.requestTypeConfig.name}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2 md:shrink-0">
              {showEdit && (
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate({ to: '/permits/new', search: { editPermitId: permitId } })
                  }
                  className="rounded-xl border-gray-200 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Pencil size={15} />
                  Edit
                </Button>
              )}
              {showDelete && (
                <Button
                  variant="outline"
                  onClick={() => setDeleteModal(true)}
                  className="rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 flex items-center gap-2"
                >
                  <Trash2 size={15} />
                  Delete
                </Button>
              )}
              {showDownload && (
                <Button
                  variant="outline"
                  onClick={() => downloadMutation.mutate()}
                  disabled={downloadMutation.isPending}
                  className="rounded-xl border-gray-200 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Download size={15} />
                  Export
                </Button>
              )}
              {showSubmitInspection && (
                <Button
                  variant="primary"
                  onClick={() => submitInspection.mutate()}
                  disabled={submitInspection.isPending}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  <Send size={15} />
                  Submit for Inspection
                </Button>
              )}
              {showSubmitApproval && (
                <Button
                  variant="primary"
                  onClick={() => submitApprovalMutation.mutate()}
                  disabled={submitApprovalMutation.isPending}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  <Send size={15} />
                  Submit for Approval
                </Button>
              )}
              {showAssignToMe && (
                <Button
                  variant="outline"
                  onClick={() => assignToMe.mutate()}
                  disabled={assignToMe.isPending}
                  className="rounded-xl border-gray-200 hover:bg-gray-100 flex items-center gap-2"
                >
                  Assign to Me
                </Button>
              )}
              {showCreateInspection && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setActiveTab('inspection');
                    setInspectionFormStarted(true);
                  }}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  <ClipboardList size={15} />
                  Create Inspection
                </Button>
              )}
              {showSubmitInspectionApproval && (
                <Button
                  variant="primary"
                  onClick={() => submitInspectionApproval.mutate()}
                  disabled={submitInspectionApproval.isPending}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  <Send size={15} />
                  {submitInspectionApproval.isPending ? 'Submitting…' : 'Submit for Approval'}
                </Button>
              )}
              {showRequestPledge && (
                <Button
                  variant="outline"
                  onClick={() => requestPledgeMutation.mutate()}
                  disabled={requestPledgeMutation.isPending}
                  className="rounded-xl border-gray-200 hover:bg-gray-100 flex items-center gap-2"
                >
                  <ClipboardList size={15} />
                  {requestPledgeMutation.isPending ? 'Requesting…' : 'Request Pledge & Study'}
                </Button>
              )}
              {showCreatePledge && (
                <Button
                  variant="primary"
                  onClick={() => {
                    setActiveTab('pledge');
                    setPledgeCreateOpen(true);
                  }}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  <ClipboardList size={15} />
                  Create Pledge & Study
                </Button>
              )}
              {showSubmitPledgeApproval && (
                <Button
                  variant="primary"
                  onClick={() => submitPledgeApprovalMutation.mutate()}
                  disabled={submitPledgeApprovalMutation.isPending}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  <Send size={15} />
                  {submitPledgeApprovalMutation.isPending ? 'Submitting…' : 'Submit for Approval'}
                </Button>
              )}
              {showApprove && (
                <Button
                  variant="primary"
                  onClick={() => setApproveModal(true)}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  Approve
                </Button>
              )}
              {showReject && (
                <Button
                  variant="destructive"
                  onClick={() => setRejectModal(true)}
                  className="rounded-xl bg-error-500 hover:bg-error-600 shadow-lg shadow-error-500/20 flex items-center gap-2"
                >
                  Reject
                </Button>
              )}
              {showSendPaymentLink && (
                <Button
                  variant="outline"
                  onClick={() => setSendLinkModal(true)}
                  className="rounded-xl border-gray-200 hover:bg-gray-100 flex items-center gap-2"
                >
                  <Mail size={15} />
                  Send Payment Link
                </Button>
              )}
              {showPay && (
                <Button
                  variant="primary"
                  onClick={() =>
                    navigate({ to: '/permits/$permitId/payment', params: { permitId } })
                  }
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  Pay
                </Button>
              )}
              {showIssue && (
                <Button
                  variant="primary"
                  onClick={() => issueMutation.mutate()}
                  disabled={issueMutation.isPending}
                  className="rounded-xl bg-brand-500 hover:bg-brand-600 shadow-lg shadow-brand-500/20 flex items-center gap-2"
                >
                  Issue Permit
                </Button>
              )}
              {showSendToSap && (
                <Button
                  variant="outline"
                  onClick={() => sapMutation.mutate()}
                  disabled={sapMutation.isPending}
                  className="rounded-xl border-gray-200 hover:bg-gray-100 flex items-center gap-2"
                >
                  Send to SAP
                </Button>
              )}
            </div>
          </div>

          {/* Status Progress */}
          <div className="mt-4 overflow-x-auto pb-2">
            <div className="min-w-[900px]">
              <StatusProgress status={permit.status} />
            </div>
          </div>

          {/* 4-column metadata grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            {[
              {
                icon: CalendarDays,
                label: 'Created',
                content: (
                  <p className="text-sm font-semibold text-gray-900">
                    {formatDateTime(permit.createdAt)}
                  </p>
                ),
              },
              {
                icon: Building2,
                label: 'Account',
                content: (
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        to: '/customers/$customerId',
                        params: { customerId: permit.account.id },
                      })
                    }
                    className="text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors flex items-center gap-1 group text-left"
                  >
                    {permit.account.name}
                    <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ),
              },
              {
                icon: UserCircle,
                label: 'Contact',
                content: (
                  <p className="text-sm font-semibold text-gray-900">{permit.contact.fullName}</p>
                ),
              },
              {
                icon: DirhamIcon,
                label: 'Total Cost',
                content: (() => {
                  const permitFee = Number.parseFloat(permit.totalFee ?? '0') || 0;
                  const studyFee = Number.parseFloat(permit.pledgeStudy?.totalFee ?? '0') || 0;
                  const combined = permitFee + studyFee;
                  return (
                    <div className="text-sm font-semibold text-gray-900">
                      {combined > 0 ? <Price value={combined} /> : '—'}
                      {studyFee > 0 && (
                        <p className="mt-0.5 text-xs font-normal text-gray-400">Permit + Study</p>
                      )}
                    </div>
                  );
                })(),
              },
            ].map((cell) => {
              const Icon = cell.icon;
              return (
                <div
                  key={cell.label}
                  className="bg-gray-50/50 rounded-xl p-4 border border-gray-200/50 flex flex-col justify-center min-h-[72px]"
                >
                  <div className="flex items-center gap-2 text-gray-400 text-xs mb-1.5">
                    <Icon className="w-3.5 h-3.5" />
                    {cell.label}
                  </div>
                  {cell.content}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action error banner */}
        {actionError && (
          <div className="border-t border-red-100 bg-red-50 px-6 py-3">
            <div className="flex items-start gap-2 text-sm text-red-700">
              <span className="flex-1">{actionError}</span>
              <button
                type="button"
                onClick={() => setActionError(null)}
                className="shrink-0 text-red-400 hover:text-red-600"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* SAP sync banner */}
        {permit.sapSync.status && (
          <div className="border-t border-gray-100 px-6 py-3">
            {permit.sapSync.status === 'SUCCESS' && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-green-100 bg-green-50 px-3 py-1 text-sm font-medium text-green-700 dark:border-green-800/30 dark:bg-green-800/20 dark:text-green-300">
                <CheckCircle2 size={14} />
                Synced to SAP — Order: {permit.sapSync.orderRef}
              </span>
            )}
            {permit.sapSync.status === 'FAILED' && (
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center rounded-full border border-red-100 bg-red-50 px-3 py-1 text-sm font-medium text-red-700 dark:border-red-800/30 dark:bg-red-800/20 dark:text-red-300">
                  SAP Sync Failed
                </span>
                {showSendToSap && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sapMutation.mutate()}
                    disabled={sapMutation.isPending}
                    className="rounded-xl border-gray-200 hover:bg-gray-100"
                  >
                    Retry
                  </Button>
                )}
              </div>
            )}
            {permit.sapSync.status === 'PENDING' && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-100 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700 dark:border-amber-800/30 dark:bg-amber-800/20 dark:text-amber-300">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
                SAP Sync Pending
              </span>
            )}
          </div>
        )}
      </div>

      {/* Sticky Tabs */}
      <div className="sticky top-16 z-10 bg-background/85 backdrop-blur-xl -mx-4 px-4 md:-mx-6 md:px-6 py-3 mb-6">
        <Tabs tabs={tabs} activeTab={activeTab} onTabChange={(id) => setActiveTab(id as Tab)} />
      </div>

      {/* ── Tab content ──────────────────────────────────────────────────── */}
      {activeTab === 'details' && <DetailsTab permit={permit} onUpdated={invalidate} />}
      {activeTab === 'fees' && showFees && <FeesTab permit={permit} showPay={showPay} />}
      {activeTab === 'documents' && (
        <DocumentsTab permit={permit} onUpdated={invalidate} can={can} />
      )}
      {activeTab === 'inspection' && showInspection && (
        <InspectionTab
          permit={permit}
          onUpdated={invalidate}
          can={can}
          currentUserId={user?.id}
          startForm={inspectionFormStarted}
          onFormStarted={() => setInspectionFormStarted(true)}
        />
      )}
      {activeTab === 'pledge' && showPledge && (
        <PledgeStudyTab permit={permit} onUpdated={invalidate} can={can} />
      )}
      {activeTab === 'approval-history' && <ApprovalHistoryTab permit={permit} />}
      {activeTab === 'audit' && showAudit && <AuditLogTab permitId={permitId} />}

      {/* ── Approve modal ─────────────────────────────────────────────── */}
      <AppModal
        open={approveModal}
        onClose={() => {
          setApproveModal(false);
          approveForm.reset();
        }}
        title="Approve Permit"
        description="Add comments before approving this permit."
      >
        <form
          onSubmit={approveForm.handleSubmit((d) => approveMutation.mutate(d))}
          className="space-y-4"
        >
          <div>
            <textarea
              className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 transition-all"
              rows={4}
              placeholder="Comments (required)"
              {...approveForm.register('comments')}
            />
            {approveForm.formState.errors.comments && (
              <p className="mt-1 text-xs text-red-500">
                {approveForm.formState.errors.comments.message}
              </p>
            )}
          </div>
          {approveMutation.isError && (
            <p className="text-xs text-red-500">Failed to approve. Try again.</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button
              variant="primary"
              className="flex-1"
              type="submit"
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? 'Approving…' : 'Approve Permit'}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setApproveModal(false);
                approveForm.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </AppModal>

      {/* ── Reject modal ──────────────────────────────────────────────── */}
      <AppModal
        open={rejectModal}
        onClose={() => {
          setRejectModal(false);
          rejectForm.reset();
        }}
        title="Reject Permit"
        description="Provide a reason for rejecting this permit."
      >
        <form
          onSubmit={rejectForm.handleSubmit((d) => rejectMutation.mutate(d))}
          className="space-y-4"
        >
          <div>
            <textarea
              className="w-full resize-none rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 hover:border-gray-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-100 transition-all"
              rows={4}
              placeholder="Reason (required)"
              {...rejectForm.register('comments')}
            />
            {rejectForm.formState.errors.comments && (
              <p className="mt-1 text-xs text-red-500">
                {rejectForm.formState.errors.comments.message}
              </p>
            )}
          </div>
          {rejectMutation.isError && (
            <p className="text-xs text-red-500">Failed to reject. Try again.</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button
              variant="destructive"
              className="flex-1"
              type="submit"
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? 'Rejecting…' : 'Reject Permit'}
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setRejectModal(false);
                rejectForm.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </AppModal>

      {/* ── Delete modal ──────────────────────────────────────────────── */}
      <AppModal
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        title="Delete Permit Request"
        description={`This will permanently delete permit ${permit?.referenceNo}. This action cannot be undone.`}
      >
        <div className="space-y-4">
          {deleteMutation.isError && (
            <p className="text-xs text-red-500">Failed to delete. Try again.</p>
          )}
          <div className="flex gap-2 pt-1">
            <Button
              variant="destructive"
              className="flex-1"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete Permit'}
            </Button>
            <Button variant="outline" onClick={() => setDeleteModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </AppModal>

      {/* ── Send Payment Link modal ───────────────────────────────────── */}
      <SendPaymentLinkDialog
        open={sendLinkModal}
        onClose={() => setSendLinkModal(false)}
        permitId={permitId}
        accountId={permit.account.id}
      />
    </AppLayout>
  );
}

// ── Send Payment Link Dialog ───────────────────────────────────────────────

type ContactRow = { id: string; fullName: string; email: string; isPrimary: boolean };

function SendPaymentLinkDialog({
  open,
  onClose,
  permitId,
  accountId,
}: {
  open: boolean;
  onClose: () => void;
  permitId: string;
  accountId: string;
}) {
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['contacts', accountId],
    queryFn: () => customersControllerFindContacts({ id: accountId }) as Promise<ContactRow[]>,
    enabled: open,
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      paymentsControllerSendPaymentLink({
        permitId,
        requestBody: { contactId: selectedContactId! },
      }) as Promise<{ sentTo: string; expiresAt: string }>,
    onSuccess: (data) => {
      setSuccessMsg(`Payment link sent to ${data.sentTo}`);
      setTimeout(() => {
        setSuccessMsg(null);
        setSelectedContactId(null);
        onClose();
      }, 2500);
    },
  });

  const handleClose = () => {
    setSelectedContactId(null);
    setSuccessMsg(null);
    sendMutation.reset();
    onClose();
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title="Send Payment Link"
      description="Select a contact to receive the payment link by email."
    >
      <div className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((n) => (
              <div key={n} className="h-14 animate-pulse rounded-xl bg-gray-100" />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setSelectedContactId(c.id)}
                className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                  selectedContactId === c.id
                    ? 'border-brand-400 bg-brand-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <p className="text-sm font-medium text-gray-900">
                  {c.fullName}
                  {c.isPrimary && (
                    <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-700">
                      Primary
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-400">{c.email}</p>
              </button>
            ))}
            {contacts.length === 0 && (
              <p className="py-4 text-center text-sm text-gray-400">
                No contacts found for this account.
              </p>
            )}
          </div>
        )}

        {successMsg && (
          <p className="rounded-xl border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
            {successMsg}
          </p>
        )}
        {sendMutation.isError && (
          <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
            {(() => {
              const err = sendMutation.error as { body?: { message?: string } } | null;
              return err?.body?.message ?? 'Failed to send. Please try again.';
            })()}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            variant="primary"
            className="flex-1"
            disabled={!selectedContactId || sendMutation.isPending || !!successMsg}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? 'Sending…' : 'Send'}
          </Button>
          <Button variant="outline" type="button" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </div>
    </AppModal>
  );
}
